/**
 * Argo OS — local database schema.
 *
 * These types describe the shape of records persisted in the browser
 * today and in Firestore tomorrow. They are intentionally close to
 * Firestore-document shape: every record has a string `id`, ISO-8601
 * timestamps for `createdAt` / `updatedAt`, and only JSON-safe values
 * (no Date objects, no Maps).
 *
 * When we migrate, the only thing that should change is the storage
 * adapter — see /migrationplan.md.
 */

// Type-only import: erased at compile time, so no runtime circular dep
// even though `agent/types.ts` imports its entity types from here.
import type { AgentAction } from '../agent/types';
// Type-only import: erased at compile time (same pattern as AgentAction),
// so the pure outreach model can live beside its CRM siblings.
import type { SequenceStep } from '../crm/outreach';
// Type-only import (same pattern): the pure signature-request engine owns
// the record shape; this file only registers its backing collection.
import type { SignatureRequest } from '../crm/signatureRequest';
// Type-only import (same pattern): the tamper-evident Decision Ledger owns
// the hash-chained record shape; this file only registers its collection.
import type { DecisionLedgerEntry } from '../agent/ledger';
// Type-only import (same pattern): the "Argo did this" activity feed owns
// its record shape; this file only registers its backing collection.
import type { AgentFeedEntry } from '../agent/activityFeed';
// Type-only import (same pattern): the scoped-automations registry owns
// its persisted record shape (Heartbeat's "approve once" automations).
import type { AutomationRecord } from '../agent/automations';

export type PersonCategory = 'customer' | 'prospect' | 'staff' | 'persona';

/**
 * One entry on a Person's activity / touch timeline. Append-only and
 * rendered most-recent-first. `kind` drives the icon + accent so a
 * skim of the timeline reads as a relationship history. Logged by hand
 * from the detail view today; sourced from email / calendar / the chat
 * agent later — the agent's writes append `system`-kind entries.
 */
export interface PersonActivity {
  id: string;
  /** ISO-8601 UTC. */
  at: string;
  kind: 'note' | 'call' | 'email' | 'meeting' | 'stage' | 'task' | 'system';
  /** Who logged it — defaults to "You". */
  actor?: string;
  /** Short description, e.g. "Left a voicemail re: pricing". */
  text: string;
}

/**
 * Shared fields across every Person record. A discriminated union on
 * `category` is overkill here because the variation between categories
 * is mostly extra optional fields, not a different shape. We model the
 * variation as flat optional fields and let the UI surface only the
 * relevant ones per category.
 */
/**
 * Result of an automated prospect evaluation (LinkedIn + website). The
 * enrichment seam (`src/lib/agent/enrich.ts`) writes this onto the
 * person; `status` lets the UI show pending / done / failed states.
 */
export interface ProspectEnrichment {
  status: 'pending' | 'done' | 'failed';
  /** One-paragraph qualitative read on the prospect / company. */
  summary?: string;
  /** Bullet signals, e.g. "Hiring 3 AEs", "Series A, $12M". */
  signals?: string[];
  /** A 0–100 fit score derived from the signals. */
  fitScore?: number;
  /** ISO-8601 UTC timestamp of the evaluation. */
  evaluatedAt?: string;
  /** Error message when status === 'failed'. */
  error?: string;
}

export interface PersonBase {
  /** Stable opaque id (uuid v4 today). Becomes the Firestore doc id. */
  id: string;
  category: PersonCategory;

  // Identity
  name: string;
  email?: string;
  phone?: string;

  // Affiliation
  title?: string;
  company?: string;

  // Web presence (drives prospect enrichment)
  /** LinkedIn profile or company-page URL. */
  linkedinUrl?: string;
  /** Company / personal website. */
  website?: string;

  // Free-form
  tags: string[];
  notes?: string;

  // CRM enrichment (super CRM) — applies to contacts + prospects.
  /** The next concrete action to take, e.g. "Send pricing one-pager". */
  nextStep?: string;
  /** ISO date (YYYY-MM-DD) to follow up by. Drives the overdue signal. */
  followUpDate?: string;
  /** Automated LinkedIn + website evaluation (prospects). */
  enrichment?: ProspectEnrichment;
  /** Append-only activity / touch timeline (most-recent-last). */
  activity?: PersonActivity[];

  // Bookkeeping (ISO-8601, UTC)
  createdAt: string;
  updatedAt: string;
}

/**
 * A single contracted deliverable EMBEDDED on a Person (customer)
 * record — the lightweight per-customer checklist edited from the
 * People view. The first-class, per-ACCOUNT deliverable object lives
 * in the `deliverables` collection (see `Deliverable` below).
 */
export interface PersonDeliverable {
  id: string;
  title: string;
  status: 'planned' | 'in-progress' | 'delivered' | 'blocked';
  /** ISO date (YYYY-MM-DD). */
  dueDate?: string;
  owner?: string;
}

/**
 * Deliverable — a first-class object per account (Ask #1): what each
 * customer is owed, its status, feeding health + the weekly update.
 * Persisted in its own `deliverables` collection via
 * `src/lib/db/deliverables.ts` (same pattern as tickets).
 */
export interface Deliverable {
  id: string;
  /** The Account this deliverable belongs to. */
  accountId: string;
  /** Denormalised for display without a join. */
  accountName: string;
  /** What the customer is owed, e.g. "Creator discovery pipeline v2". */
  name: string;
  /** Argo-side owner responsible for delivering it. */
  owner?: string;
  /** ISO date (YYYY-MM-DD); null = no committed date. */
  dueDate?: string | null;
  status: 'owed' | 'in-progress' | 'delivered' | 'accepted';
  note?: string;
  // Bookkeeping (ISO-8601, UTC)
  createdAt: string;
  updatedAt: string;
}

/** A scheduled billing / invoice milestone on a customer. */
export interface BillingDate {
  id: string;
  label: string;
  /** ISO date (YYYY-MM-DD). */
  date: string;
  /** USD amount due. */
  amount?: number;
  status?: 'scheduled' | 'invoiced' | 'paid' | 'overdue';
}

/** A point-of-contact on a customer or account (multi-POC support). */
export interface ContactPOC {
  id: string;
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  /** Exactly one POC should be primary. */
  primary?: boolean;
}

/** Customer-specific fields. */
export interface CustomerExtension {
  status?: 'active' | 'paused' | 'churned';
  /** USD, annualised. */
  accountValue?: number;
  /** ISO date string (YYYY-MM-DD). */
  contractStart?: string;
  /** ISO date the contract renews / expires. */
  renewalDate?: string;
  /** Argo team owner. */
  ownerName?: string;
  /** Contracted deliverables tracked to completion (embedded). */
  deliverables?: PersonDeliverable[];
  /** Scheduled billing milestones. */
  billingDates?: BillingDate[];
  /** Multiple points of contact (beyond the primary `name`/`email`). */
  pocs?: ContactPOC[];
  /**
   * ISO-8601 timestamp of the last governed weekly-update email queued for
   * this customer (stamped by the `send_customer_update` executor). Drives
   * the weekly-update agent's 6-day cadence guard.
   */
  lastUpdateSentAt?: string;
}

/** Prospect-specific fields. */
export interface ProspectExtension {
  stage?: 'new' | 'qualified' | 'proposal' | 'closed-won' | 'closed-lost';
  source?: string;
  /** USD estimate of deal size. */
  estimatedValue?: number;
  /** ISO date string of last meaningful touch. */
  lastTouch?: string;
  /** 0–100 lead score (see src/lib/crm/score.ts). */
  score?: number;
  /**
   * Why the deal closed — captured when stage moves to closed-won or
   * closed-lost. The single most valuable training signal the pipeline
   * produces; rolled up by `crm/winloss.ts` into win-rate intelligence
   * and fed back into proposal findings. One of the reason taxonomy
   * keys (see WON_REASONS / LOST_REASONS), or free text via "Other".
   */
  winLossReason?: string;
  /** Optional free-text colour on the win/loss (the deal's story). */
  winLossNote?: string;
  /** ISO date the deal closed (set when stage → closed-won/-lost). */
  closedAt?: string;
}

/** Staff-specific fields. */
export interface StaffExtension {
  role?: string;
  team?: string;
  /** ISO date string. */
  startDate?: string;
  location?: string;
}

/**
 * Structured ideal-customer-profile definition that fleshes out a
 * persona beyond a one-line summary. Every field is optional so a
 * persona can be as light or as detailed as the user wants; the
 * synthesizer and the starter templates populate as many as they can.
 */
export interface PersonaProfile {
  /** Primary job role / function, e.g. "VP of RevOps". */
  role?: string;
  /** Seniority band — drives tone and which proof points land. */
  seniority?: string;
  /** Headcount band of the companies this persona works at. */
  companySize?: string;
  /** Industry / vertical. */
  industry?: string;
  /** What success looks like for them. */
  goals?: string[];
  /** The problems that make them open to a conversation. */
  painPoints?: string[];
  /** What drives the decision emotionally / professionally. */
  motivations?: string[];
  /** Likely push-back you have to answer. */
  objections?: string[];
  /** What they weigh when choosing a vendor. */
  decisionCriteria?: string[];
  /** Where you actually reach them. */
  channels?: string[];
  /** Metrics they are measured on. */
  kpis?: string[];
  /** A representative "in their words" line. */
  quote?: string;
}

/** Persona-specific fields. */
export interface PersonaExtension {
  archetype?: string;
  /** Free-form overview shown above the structured profile. */
  summary?: string;
  /** Structured ICP definition (goals, pains, objections, …). */
  profile?: PersonaProfile;
}

/**
 * Concrete Person record stored in the `people` collection. All
 * extensions are merged in flat — every record has the union of all
 * possible fields, but only the ones relevant to its `category` will be
 * populated.
 */
export interface Person
  extends PersonBase,
    CustomerExtension,
    ProspectExtension,
    StaffExtension,
    PersonaExtension {}

// ===========================================================================
// Accounts — pipeline-shaped, account-based (not contact-based)
// ===========================================================================

/**
 * Pipeline stages used by the Kanban view. Order matters here — it is
 * the source of truth for the column order in the board.
 *
 * The labels were lifted directly from the Client Lifecycle CSV. The
 * three "phase" groups (Sales / Implementations / Customer Success) are
 * captured in `STAGE_PHASE` below so the UI can color-code columns
 * without storing redundant info on each Account.
 */
export const PIPELINE_STAGES = [
  'Discovery',
  'Sales Review',
  'Deal Signed',
  'Customer Data Shared',
  'Kickoff & Detailed Scoping',
  'Initial Solutioning',
  'Systems Integration/Access',
  'Customer Portal',
  'Initial Outcome Delivery',
  'Customer Feedback',
  '2nd Iteration',
  'Training',
  '1st Invoice',
  'Intro to CS/AM',
  'Iterative Improvement',
  'Monthly Reporting',
  'Advocacy',
] as const;

export type PipelineStage = (typeof PIPELINE_STAGES)[number];

export type PipelinePhase = 'sales' | 'implementations' | 'customer-success';

/** Which phase each stage belongs to — derived from the CSV header. */
export const STAGE_PHASE: Record<PipelineStage, PipelinePhase> = {
  Discovery: 'sales',
  'Sales Review': 'sales',
  'Deal Signed': 'sales',
  'Customer Data Shared': 'sales',
  'Kickoff & Detailed Scoping': 'implementations',
  'Initial Solutioning': 'implementations',
  'Systems Integration/Access': 'implementations',
  'Customer Portal': 'implementations',
  'Initial Outcome Delivery': 'implementations',
  'Customer Feedback': 'implementations',
  '2nd Iteration': 'implementations',
  Training: 'implementations',
  '1st Invoice': 'implementations',
  'Intro to CS/AM': 'customer-success',
  'Iterative Improvement': 'customer-success',
  'Monthly Reporting': 'customer-success',
  Advocacy: 'customer-success',
};

/**
 * One workflow / engagement on an account. The CSV models a row per
 * workflow, so an account with three workflows has three sub-status
 * lines. Each workflow is tracked through its own pipeline stage —
 * an account's workflows can sit in different stages (e.g. Drift's
 * Creator Discovery is in Iterative Improvement while Platform
 * Integration is still in Customer Feedback). The Workflows kanban
 * is the primary surface for seeing this; the CRM-style accounts
 * view rolls workflows up under the parent account.
 */
export interface AccountWorkflow {
  /** Stable id so the editor can identify the row. */
  id: string;
  name: string;
  /** Free-form short status, e.g. "Delivery working in hybrid". */
  status?: string;
  /**
   * Pipeline stage for THIS workflow. The kanban operates on this
   * field; account-level stage is now derived (the earliest workflow
   * stage in the canonical order — see `accountRollupStage`).
   */
  stage: PipelineStage;
}

/**
 * A single audit-trail entry. We append one whenever a stage moves,
 * a workflow is added/edited, or the POC changes — driven by the
 * Kanban + detail editors. UI is timeline-style.
 */
export interface AuditEntry {
  id: string;
  /** ISO-8601 UTC. */
  at: string;
  /** Free-form actor — defaults to "You" until we have auth. */
  actor?: string;
  /** Short past-tense description, e.g. "Moved Discovery → Sales Review". */
  text: string;
}

/**
 * Lightweight email-trail entry. Today this is hand-edited from the
 * detail view; later it'll be sourced from the Gmail MCP.
 */
export interface EmailEntry {
  id: string;
  /** ISO-8601 UTC. */
  at: string;
  direction: 'in' | 'out';
  subject: string;
  /** Sender name + email rolled into one line, e.g. "Adam <adam@argoiq.com>". */
  from?: string;
  to?: string;
  preview?: string;
}

/**
 * Account — the central object the Kanban view operates on.
 *
 * Account-based, not contact-based: a single account row owns the
 * primary POC inline, a list of secondary contacts, the current stage,
 * and the workflow / email / audit trails. We deliberately keep POC as
 * a flat record on the account (rather than a separate `people`
 * relation) so the Kanban card can render it without a join.
 */
export interface Account {
  id: string;
  name: string;
  /**
   * @deprecated Stages now live on each workflow. Kept on the Account
   * for migration safety; new code should use `accountRollupStage` to
   * derive a single representative stage from the workflows list.
   */
  stage?: PipelineStage;

  /** Primary point-of-contact on the customer side. */
  pocName?: string;
  pocTitle?: string;
  pocEmail?: string;
  pocPhone?: string;

  /** Additional points of contact beyond the primary POC. */
  secondaryContacts?: ContactPOC[];

  /** Argo team owner (defaults to whichever rep is assigned). */
  ownerName?: string;
  /** Free-form assignment string when more than one owner is involved. */
  assignedTo?: string;

  /** Per-workflow status lines pulled from the lifecycle CSV. */
  workflows: AccountWorkflow[];

  /** Free-form notes — the detail view's main writing surface. */
  notes?: string;

  /**
   * Soft-archive flag. Optional + additive: existing records (undefined)
   * are treated as active. Archived clients are hidden from the Clients
   * directory but the record is preserved (archive, not delete).
   */
  archived?: boolean;

  /** Audit + email histories (most-recent-first when rendered). */
  audit: AuditEntry[];
  emails: EmailEntry[];

  /** Bookkeeping (ISO-8601 UTC). updatedAt is what the card surfaces. */
  createdAt: string;
  updatedAt: string;
}

/**
 * Derive a single representative stage for an account from its
 * workflows. We pick the *earliest* stage in the canonical pipeline
 * order — the rationale being that an account's overall progress is
 * gated by its least-advanced engagement. Returns `undefined` if the
 * account has no workflows yet.
 */
export function accountRollupStage(account: Account): PipelineStage | undefined {
  if (account.workflows.length === 0) return account.stage;
  let earliest: PipelineStage | undefined;
  let earliestIdx = Infinity;
  for (const wf of account.workflows) {
    const idx = (PIPELINE_STAGES as readonly string[]).indexOf(wf.stage);
    if (idx >= 0 && idx < earliestIdx) {
      earliestIdx = idx;
      earliest = wf.stage;
    }
  }
  return earliest;
}

// ===========================================================================
// AppUsers — admin-managed user records
// ===========================================================================

export type AppUserRole = 'admin' | 'staff' | 'viewer';
export type AppUserStatus = 'active' | 'pending' | 'disabled';

/**
 * One row in the Admin > Users table. Mirrors the shape of
 * `users/{uid}` in Firestore at launch (see implementationplan.md §5).
 *
 * The mock auth provider matches users by `email`; once Firebase Auth
 * is live the canonical id becomes the Auth `uid` and `email` is just
 * a denormalized display field.
 */
export interface AppUser {
  /** Stable id (becomes Firebase Auth uid). */
  id: string;
  email: string;
  displayName: string;
  role: AppUserRole;
  status: AppUserStatus;
  /** ISO-8601 timestamp of last successful sign-in. */
  lastSignInAt?: string;
  /** Free-form note an admin can attach to a user. */
  notes?: string;
  /** Bookkeeping. */
  createdAt: string;
  updatedAt: string;
}

// ===========================================================================
// Tickets — the live ticketing system (specs, owners, estimates,
// deadlines, and an append-only timeline). Worked by humans (Trent,
// Grant) and Claude agents.
// ===========================================================================

export type TicketStatus =
  | 'backlog'
  | 'open'
  | 'claimed'
  | 'in-progress'
  | 'blocked'
  | 'review'
  | 'done'
  | 'cancelled';

export type TicketPriority = 'p0' | 'p1' | 'p2' | 'p3';

/**
 * One entry in a ticket's append-only timeline. Mirrors `AuditEntry`
 * (above) but with a typed `kind` so the UI can render an icon per
 * event type.
 */
export interface TicketEvent {
  id: string;
  /** ISO-8601 UTC. */
  at: string;
  /** Who triggered it — e.g. "Grant" or "claude:fixer". */
  actor: string;
  kind: 'created' | 'status' | 'claimed' | 'assigned' | 'comment' | 'updated';
  /** Past-tense description, e.g. "Status: open → in-progress". */
  text: string;
}

/** A free-form comment on a ticket. */
export interface TicketComment {
  id: string;
  /** ISO-8601 UTC. */
  at: string;
  author: string;
  body: string;
}

/**
 * A ticket. `id` is a human-friendly sequential key ("TIQ-001"). The
 * `owner` is the person or agent currently responsible; `null` until
 * someone claims it. Estimate + deadline drive the time columns in the
 * UI (overdue deadlines are highlighted).
 */
export interface Ticket {
  id: string;
  title: string;
  /** Markdown spec / description. */
  spec: string;
  status: TicketStatus;
  priority: TicketPriority;
  /** Person ("Trent"/"Grant") or agent ("claude:fixer"); null = unclaimed. */
  owner: string | null;
  createdBy: string;
  /** Time estimate in hours; null = not estimated. */
  estimateHours: number | null;
  /** ISO date string; null = no deadline. */
  deadline: string | null;
  /** ISO timestamp work started (status → in-progress); null otherwise. */
  startedAt: string | null;
  labels: string[];
  /** Append-only timeline (most-recent-last). */
  timeline: TicketEvent[];
  comments: TicketComment[];
  createdAt: string;
  updatedAt: string;
}

// ===========================================================================
// Documents — proposals (for prospects) and contracts (for customers).
// One collection, discriminated on `kind`, attachable to any entity
// (an account or a person). Bodies are markdown so they can be edited
// inline, generated from a template, and later exported to PDF / e-sign.
// ===========================================================================

export type DocumentKind = 'proposal' | 'contract';

/**
 * Lifecycle of a document. Proposals and contracts share the set; the
 * UI surfaces the subset relevant to each kind (e.g. contracts use
 * `signed`, proposals use `accepted`).
 */
export type DocumentStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'accepted'
  | 'signed'
  | 'declined'
  | 'expired';

/** What a document is attached to. */
export type DocumentEntityType = 'account' | 'person';

/**
 * A proposal or contract. `body` is markdown — generated from a
 * template on create, then editable inline. `value` is the deal /
 * contract amount in USD. `entity*` fields denormalize the linked
 * account or person so the document list renders without a join.
 */
export interface BusinessDoc {
  id: string;
  kind: DocumentKind;
  title: string;
  status: DocumentStatus;

  /** Linked entity (denormalized for join-free rendering). */
  entityType: DocumentEntityType;
  entityId: string;
  entityName: string;

  /** Markdown body. */
  body: string;

  /** USD deal / contract value; null = unspecified. */
  value?: number;
  /** ISO date the document is valid until / expires. */
  validUntil?: string;
  /** Argo team owner. */
  ownerName?: string;
  /** Id of the template used to generate the body. */
  templateId?: string;

  /**
   * ISO timestamp the document FIRST entered a won state (a proposal
   * 'accepted' or a contract 'signed'). Distinct from createdAt (when the
   * doc was drafted, possibly weeks earlier) so downstream automation — the
   * onboarding playbook — can anchor its relative due dates to when the deal
   * actually closed rather than when the paperwork was written. Set once,
   * preserved across later status edits; undefined until the doc is won.
   */
  acceptedAt?: string;

  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

// ===========================================================================
// Project tasks — the project-management layer on top of Workflows.
//
// Tickets track engineering work (specs, agents, estimates). Tasks are
// the lighter-weight PM primitive: a to-do with an owner, a due date,
// a checklist of subtasks, an optional recurrence, and an append-only
// timeline. Tasks can hang off an account / workflow so a project's
// to-dos live next to the kanban that tracks its delivery.
// ===========================================================================

export type TaskStatus = 'todo' | 'in-progress' | 'blocked' | 'done';

export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

/**
 * How a task repeats. On completing a recurring task we spawn the next
 * instance with the due date rolled forward by the cadence; the
 * completed copy stays in history. `none` = one-shot.
 */
export type TaskRecurrence = 'none' | 'daily' | 'weekly' | 'monthly';

/** A single checklist item / subtask on a task. */
export interface TaskChecklistItem {
  id: string;
  text: string;
  done: boolean;
}

/**
 * One entry in a task's append-only timeline. Mirrors TicketEvent but
 * scoped to the PM verbs a task goes through.
 */
export interface TaskEvent {
  id: string;
  /** ISO-8601 UTC. */
  at: string;
  actor: string;
  kind: 'created' | 'status' | 'assigned' | 'note' | 'checklist' | 'recurred';
  /** Past-tense description, e.g. "Status: todo → done". */
  text: string;
}

/**
 * A project task. `dueDate` is a plain ISO date (YYYY-MM-DD) so the
 * overdue signal compares on day granularity. `assignee` is a free-form
 * name (person or agent) to stay consistent with Ticket.owner. The
 * optional `accountId` / `workflowId` link a task to the delivery it
 * supports so the board can group by project.
 */
export interface ProjectTask {
  id: string;
  title: string;
  /** Free-form details / notes (markdown-ish, rendered as plain text). */
  notes?: string;
  status: TaskStatus;
  priority: TaskPriority;
  /** Person ("Trent") or agent ("claude:worker"); null = unassigned. */
  assignee: string | null;
  /** ISO date string (YYYY-MM-DD); null = no due date. */
  dueDate: string | null;
  recurrence: TaskRecurrence;
  checklist: TaskChecklistItem[];

  /**
   * Soft-archive flag. Optional + additive: existing records (undefined)
   * are treated as active. Archived tasks are hidden from the board and
   * roll-up counts but the record is preserved (archive, not delete).
   */
  archived?: boolean;

  /**
   * Task ids this task depends on (dependency edges: blocker → this).
   * Optional + additive: tasks without it behave exactly as today.
   * Consumed by the pure dependency engine in `crm/taskGraph.ts`
   * (topo order, critical path, slip detection). Dangling ids (deleted
   * tasks) are ignored by the graph, never an error.
   */
  blockedBy?: string[];

  /** Optional link to the account / workflow this task supports. */
  accountId?: string;
  accountName?: string;
  workflowId?: string;
  workflowName?: string;

  /** Optional link to the contact (customer or prospect) this task is for. */
  personId?: string;
  personName?: string;

  /**
   * Optional link to the engineering ticket this task tracks
   * (Ticket.id, e.g. "TIQ-012"). One task ↔ one ticket keeps the
   * mental model simple: the ticket is the spec, the task is the
   * PM-side handle on it. Roadmap milestones roll BOTH up via
   * `RoadmapItem.linkedTicketIds` / `linkedTaskIds`.
   */
  ticketId?: string;

  /**
   * Optional home Project (Clients → Projects → Tasks). Additive: tasks
   * with only `accountId`/`workflowId` keep working exactly as before.
   */
  projectId?: string;
  projectName?: string;

  /** Free-form labels for board grouping + filtering. */
  tags?: string[];

  /** Threaded discussion (Phase 3). */
  comments?: WorkComment[];
  /** File + link attachments (Phase 3). */
  attachments?: WorkAttachment[];
  /** Logged time entries (Phase 5). */
  timeEntries?: TaskTimeEntry[];

  /** ISO timestamp the task was completed; null until done. */
  completedAt?: string | null;
  createdBy?: string;
  /** Append-only timeline (most-recent-last). */
  timeline: TaskEvent[];
  createdAt: string;
  updatedAt: string;
}

// ===========================================================================
// Projects — the delivery workspace layer (Clients → Projects → Tasks).
// A Project belongs to a Client (an Account) and groups ProjectTasks.
// Argo had no first-class Project entity; this is the internal-tool
// hierarchy modeled on CollabTask. Collaboration (comments/attachments)
// and time entries are inline arrays, consistent with how Account/Task
// already carry timeline/checklist/emails.
// ===========================================================================

/** A file or link attachment pinned to a project or task. */
export interface WorkAttachment {
  id: string;
  kind: 'file' | 'link';
  label: string;
  url: string;
  /** MIME type for uploaded files; omitted for links. */
  mime?: string;
  /** Size in bytes for uploaded files; omitted for links. */
  size?: number;
  addedBy: string;
  /** ISO-8601 UTC. */
  addedAt: string;
}

/**
 * One threaded comment on a project or task. `mentions` holds the
 * assignee-roster names @mentioned in `body` — the list that drives
 * real notifications (never fabricated: only names actually parsed out).
 */
export interface WorkComment {
  id: string;
  authorName: string;
  body: string;
  mentions: string[];
  /** ISO-8601 UTC. */
  createdAt: string;
}

/** A single logged time entry on a task. */
export interface TaskTimeEntry {
  id: string;
  /** Who logged it (assignee-roster name). */
  who: string;
  /** Minutes logged. */
  minutes: number;
  note?: string;
  /** ISO-8601 UTC the entry was recorded. */
  at: string;
}

/** An external link pinned to a project. */
export interface ProjectLink {
  id: string;
  label: string;
  url: string;
}

export type ProjectStatus = 'active' | 'on-hold' | 'completed' | 'archived';

/**
 * A delivery Project: the container between a Client (Account) and its
 * Tasks. `clientId` → Account.id; `clientName` is denormalized for board
 * legibility (matches how ProjectTask stores accountName/workflowName).
 * Completion % is derived from the project's tasks at read time, never
 * stored.
 */
export interface Project {
  id: string;
  clientId: string;
  clientName: string;
  title: string;
  description?: string;
  status: ProjectStatus;
  priority: TaskPriority;
  /** ISO date (YYYY-MM-DD); null = no due date. */
  dueDate?: string | null;
  tags: string[];
  /** Internal owner (assignee-roster name). */
  ownerName?: string;
  /** Internal collaborators (assignee-roster names). */
  memberNames: string[];
  links: ProjectLink[];
  comments: WorkComment[];
  attachments: WorkAttachment[];
  createdBy?: string;
  /** Append-only timeline (reuses the task event shape). */
  timeline: TaskEvent[];
  createdAt: string;
  updatedAt: string;
}

// ===========================================================================
// Deployed agents — the registry of Claude agents launched from inside
// the app. A DeployedAgent is a *configured, named* worker (built from a
// template) with a cadence and a run history. It is distinct from the
// transient ticket-autopilot agents: those are the personas
// ("claude:worker") that pick up tickets, whereas a DeployedAgent is the
// long-lived, user-managed instance bound to a persona and a job. The
// Agents tab under Projects is its management surface.
// ===========================================================================

/** Built-in agent blueprints a user can deploy from. */
export type AgentTemplateId =
  | 'ticket-worker'
  | 'researcher'
  | 'outreach-monitor'
  | 'system-scanner'
  | 'customer-success'
  | 'document-chase'
  // Ingestion / intelligence
  | 'market-research'
  | 'icp-builder'
  | 'web-scraper'
  | 'linkedin-monitor'
  | 'x-monitor'
  | 'six-sigma-analyst'
  // Content
  | 'content-writer'
  | 'outreach-sequencer'
  // ClickUp-style ops
  | 'task-router'
  | 'deadline-watcher'
  | 'standup-reporter'
  | 'custom';

/** How often a deployed agent runs. */
export type AgentCadence = 'manual' | 'continuous' | 'hourly' | 'daily';

/** Lifecycle of a deployed agent. */
export type AgentRunState = 'running' | 'paused' | 'idle';

/**
 * One entry in a deployed agent's append-only run / activity log.
 * Mirrors TicketEvent but scoped to the deploy / run / status verbs.
 */
export interface AgentRunEntry {
  id: string;
  /** ISO-8601 UTC. */
  at: string;
  actor: string;
  kind: 'deployed' | 'run' | 'status' | 'note';
  /** Past-tense description, e.g. "Ran — swept 12 tickets, claimed 2". */
  text: string;
}

/**
 * A deployed agent. `persona` ties it to one of the autopilot identities
 * ("claude:worker"); `template` records the blueprint it was built from.
 * `cadence` + `state` drive the UI controls; `timeline` is the run
 * history (most-recent-last).
 */
export interface DeployedAgent {
  id: string;
  name: string;
  template: AgentTemplateId;
  /** Bound autopilot persona, e.g. "claude:worker". */
  persona: string;
  /** What this agent does — shown on the card. */
  description: string;
  cadence: AgentCadence;
  state: AgentRunState;
  createdBy: string;
  /** ISO timestamp of the most recent run; null until first run. */
  lastRunAt: string | null;
  /**
   * ISO timestamp of the next run implied by the cadence, stamped after
   * every run; null for `manual` (never auto-runs). Optional + additive
   * so pre-existing records need no migration.
   */
  nextRunAt?: string | null;
  /**
   * Per-agent live schedule: when true, the app's 60s ticker
   * (src/lib/agent/useLiveSchedule.ts) runs this agent on its cadence
   * while the app is open — independent of the GLOBAL autonomy switch.
   * Safe-by-design: runs only queue proposals, never auto-write.
   */
  liveSchedule?: boolean;
  /** Total completed runs. */
  runCount: number;
  /** Append-only run / activity log (most-recent-last). */
  timeline: AgentRunEntry[];
  createdAt: string;
  updatedAt: string;
}

// ===========================================================================

/**
 * Where a roadmap item sits on the now/next/later horizon. `shipped`
 * is the archive lane for completed work that still tells the story.
 */
export type RoadmapStage = 'now' | 'next' | 'later' | 'shipped';

/** Delivery status of a single roadmap milestone. */
export type MilestoneStatus = 'planned' | 'in-progress' | 'blocked' | 'done';

/**
 * One roadmap milestone. Stages group items into now/next/later/shipped;
 * `goal` ties a milestone to a higher-order theme it ladders up to.
 * `progress` is a 0–100 completion estimate driving the bar in the UI.
 */
export interface RoadmapItem {
  id: string;
  title: string;
  /** One- or two-line summary of the milestone. */
  description?: string;
  stage: RoadmapStage;
  status: MilestoneStatus;
  /** Higher-order goal / theme this milestone serves. */
  goal?: string;
  /** Display name of the owner (free text or an app user). */
  assignee?: string;
  /** ISO date (YYYY-MM-DD) the work starts. */
  startDate?: string;
  /** ISO date (YYYY-MM-DD) the work is targeted to land. */
  targetDate?: string;
  /** 0–100 completion estimate. */
  progress?: number;
  /** Ticket ids this milestone rolls up. */
  linkedTicketIds?: string[];
  /**
   * Task ids this milestone rolls up. When a milestone has ANY linked
   * tickets/tasks, its progress is auto-computed from their completion
   * (see src/lib/crm/milestones.ts) and the manual `progress` field is
   * ignored — the roadmap reports reality instead of an estimate.
   */
  linkedTaskIds?: string[];
  createdAt: string;
  updatedAt: string;
}

/** Outbox entry for email delivery via Cloud Function (sendEmail). */
export interface OutboxDoc {
  id: string;
  /** Email recipient. */
  to: string;
  /** Email subject line. */
  subject: string;
  /** Plain-text body. */
  textContent?: string;
  /** HTML body (optional, falls back to textContent). */
  htmlContent?: string;
  /** Status: 'queued' → 'sent' | 'error'. */
  status?: 'queued' | 'sent' | 'error';
  /** Message ID from email service (set after successful send). */
  messageId?: string | null;
  /** Error message if status === 'error'. */
  error?: string;
  /** ISO-8601 timestamp when queued. */
  createdAt: string;
  /** ISO-8601 timestamp of last update. */
  updatedAt: string;
}

/**
 * One learned operator preference — agent memory.
 *
 * Distilled from how proposed actions were handled: dismissing or
 * reverting a kind of proposal repeatedly is a signal the agent should
 * adapt to. Records are upserted per (actionKind, signal) pair with a
 * running count; the chat injects the distilled `note` lines into the
 * worker context so a connected Claude proposes with the operator's
 * track record in mind. This is app bookkeeping about the operator's own
 * choices (like the autonomy streaks), NOT an agent-proposed write — so
 * it does not go through a confirm card.
 */
export interface AgentPref {
  id: string;
  /** Action kind the preference is about, e.g. 'create_task'. */
  actionKind: string;
  /** What the operator did with proposals of this kind. */
  signal: 'dismissed' | 'reverted';
  /** Distilled, human-readable preference line (what gets injected). */
  note: string;
  /** How many times this signal has been seen for this kind. */
  count: number;
  /** Display name of the operator whose action last updated this. */
  operator?: string;
  createdAt: string;
  updatedAt: string;
}

/** One pending approval from a scheduler or autopilot. */
export interface ApprovalItem {
  id: string;
  /**
   * The action being proposed. Typed as the agent action union — this is
   * a type-only import, so it is erased at compile time and creates no
   * runtime circular dependency with `agent/types.ts`.
   */
  action: AgentAction;
  /** Source of the proposal: 'scheduler' | 'autopilot' | 'chat' */
  source: 'scheduler' | 'autopilot' | 'chat';
  /** Optional context/note about why this action was proposed. */
  note?: string;
  /**
   * Render-ready, channel-agnostic label for the proposed action, stamped at
   * queue time via `summarizeAction`. Denormalized on purpose: a server-side
   * consumer (the Slack notifier Cloud Function, a future Teams/email digest)
   * can render the proposal without importing the executor layer or
   * re-deriving the label. Optional + additive, so old records (and the
   * schema-v1 baseline) need no migration.
   */
  summary?: { verb: string; detail: string };
  /**
   * One-sentence "why this proposal" from the proposer (worker or a
   * deterministic agent). Optional + additive — older proposals without it
   * render exactly as before, no migration.
   */
  rationale?: string;
  /**
   * Concrete evidence lines backing the proposal (e.g. "Follow-up 21 days
   * overdue"). Rendered as bullets in the approval inbox's "Why?" section.
   * Optional + additive like `rationale`.
   */
  evidence?: string[];
  /** Status: pending until the operator approves or dismisses. */
  status: 'pending' | 'approved' | 'dismissed';
  /** ISO-8601 UTC timestamp when created. */
  createdAt: string;
  /** ISO-8601 UTC timestamp when resolved (approved or dismissed). Null until then. */
  resolvedAt?: string | null;
  /**
   * What actually HAPPENED after approval — the honest bridge from
   * "influenced" toward "proven" (did the follow-up get a reply? did the
   * at-risk account renew?). Recorded by hand today via
   * `recordOutcome` (src/lib/agent/valueLedger.ts); sourced from email /
   * CRM signals later. Absent means unknown, which the rollup treats as
   * 'pending' — never assumed positive. Optional + additive, so existing
   * records need no migration.
   */
  outcome?: ActionOutcome;
}

/** Outcome status for an approved action. Unknown defaults to 'pending'. */
export type ActionOutcomeStatus = 'pending' | 'positive' | 'negative' | 'no_response';

/**
 * The recorded real-world result of an approved agent action. Kept
 * JSON-safe (ISO timestamp, no Dates) like every other persisted shape.
 */
export interface ActionOutcome {
  status: ActionOutcomeStatus;
  /** Optional colour, e.g. "replied, booked call" or "account churned". */
  note?: string;
  /** ISO-8601 UTC timestamp when the outcome was recorded. */
  recordedAt: string;
}

/**
 * One daily AQI reading — the raw material for the Dashboard trend
 * sparkline. Exactly one row per local calendar day (id === date), the
 * latest reading of the day winning, so the history is one honest point
 * per day rather than a per-render log.
 */
export interface AqiSnapshot {
  /** id === the local date key (YYYY-MM-DD): one snapshot per day. */
  id: string;
  /** Local calendar date the snapshot represents (YYYY-MM-DD). */
  date: string;
  /** The blended 0–100 AQI for that day (latest reading wins). */
  score: number;
  /** Per-component scores, kept for a future per-lever trend. */
  components: { key: string; score: number }[];
  /** ISO-8601 UTC timestamp of the last write for this day. */
  updatedAt: string;
}

/**
 * One daily customer-health reading — the raw material for the CS trend
 * ("we moved health 4→8"). Mirrors `AqiSnapshot`: exactly one row per
 * customer per local calendar day (id === `${personId}:${date}`), the
 * latest reading of the day winning, so the history is one honest point
 * per customer per day rather than a per-render log.
 */
export interface CustomerHealthSnapshot {
  /** id === `${personId}:${date}`: one snapshot per customer per day. */
  id: string;
  /** The customer (Person) this reading belongs to. */
  personId: string;
  /** Local calendar date the snapshot represents (YYYY-MM-DD). */
  date: string;
  /** The 0–100 health score for that day (latest reading wins). */
  score: number;
  /** Coarse band for colour-coding (see src/lib/crm/health.ts). */
  band: 'at-risk' | 'watch' | 'healthy';
  /** ISO-8601 UTC timestamp of the last write for this day. */
  updatedAt: string;
}

/**
 * Company Brain — the per-account source of truth every CRM agent reads
 * from and (later) writes back to. Each fact is versioned and carries its
 * own provenance: where it came from, how sure we are, and who set it.
 * This is the honesty contract — nothing in the Brain is a bare value.
 */

/** Where a Brain fact came from (founder-stated = fully trusted). */
export type BrainFactSource =
  | 'founder' // told to us by the customer directly — confidence 1.0
  | 'operator' // entered/confirmed by an Argo operator
  | 'research' // pulled from external research / enrichment
  | 'outcome' // derived from a real outcome (a closed deal, a reply, etc.)
  | 'inferred'; // proposed by a model — a proposal until confirmed

/** The typed sections of an account's Brain. */
export type BrainSection =
  | 'identity' // who they are: brand, domains, size, key people
  | 'positioning' // value prop, differentiators, competitors
  | 'icp' // who they sell to: segments, personas, pains
  | 'goals' // what they're trying to achieve (ties actions to outcomes)
  | 'relationships' // POCs, champions, blockers, decision process
  | 'commercial' // deal size, pricing, renewal, deliverables, billing
  | 'history' // key events, meetings, outcomes over time
  | 'learned' // what's worked / not worked, with evidence
  | 'rules'; // operator guardrails for this account

/**
 * A single versioned, sourced fact in an account's Brain. The id is
 * deterministic (`${accountId}:${section}:${field}`) so writes upsert the
 * current "head" while `brainEvents` keeps the full append-only history.
 */
export interface BrainFact {
  id: string;
  accountId: string;
  section: BrainSection;
  /** Stable field key within the section, e.g. 'valueProp'. */
  field: string;
  /** Human-readable label for the field. */
  label: string;
  /** The fact value. String for v1; structured values can come later. */
  value: string;
  source: BrainFactSource;
  /** 0–1; founder-stated = 1.0, inferred < 1.0. */
  confidence: number;
  /** Optional pointer to the evidence: a url, doc id, ticket id, note. */
  evidence?: string;
  /** Monotonic per (account, section, field). */
  version: number;
  /** Agent name or 'operator'. */
  setBy: string;
  /** ISO-8601 UTC. */
  setAt: string;
}

/** Append-only change-log entry for the Brain (who changed what, when). */
export interface BrainEvent {
  id: string;
  accountId: string;
  section: BrainSection;
  field: string;
  oldValue?: string;
  newValue: string;
  source: BrainFactSource;
  /** Who wrote it: an agent name or 'operator'. */
  agent: string;
  at: string;
  /**
   * Set when a write was refused because it would have lowered the
   * confidence of an existing fact — surfaced as a conflict to resolve
   * rather than silently overwriting. The fact itself is left unchanged.
   */
  conflict?: boolean;
}

/**
 * A governance activity entry — the append-only audit trail behind Argo's
 * "governed writes" promise. Every action approved or rejected, every approved
 * action executed, and every agent run writes one entry here. It is
 * append-only by contract (this file exposes no update/remove for it) and
 * computed from real events, never seeded — so an empty log honestly means
 * nothing governed has happened yet.
 */
export interface ActivityEntry {
  id: string;
  /** ISO timestamp of the event (field named `at`, like account audit rows). */
  at: string;
  event: 'proposed' | 'approved' | 'rejected' | 'executed' | 'undone' | 'agent_run';
  /** Human-readable one-liner (reuses the action summary / agent run text). */
  summary: string;
  /** Origin: the chat, a scheduler/autopilot/deployed agent, or the system. */
  source: 'chat' | 'scheduler' | 'autopilot' | 'agent' | 'system';
  /** AgentAction.kind for action events; the agent template for runs. */
  kind?: string;
  /** Related record id: the approval id for actions, the agent id for runs. */
  subjectId?: string;
  /**
   * Tamper-evidence (hash chain). `hash` is a stable digest over this entry's
   * canonical fields (at,event,source,kind,summary,subjectId) + `prevHash`;
   * `prevHash` is the previous entry's `hash` ('' for the genesis entry).
   * Optional + best-effort: a hashing failure never blocks the append, and
   * entries written before this feature simply have neither field.
   */
  hash?: string;
  prevHash?: string;
}

/**
 * One step in an Orchestrator plan. A `read` step gathers context (auto, no
 * approval); a `write` step carries a governed AgentAction that flows through
 * the SAME approvals gate as everything else — the Orchestrator can never
 * bypass governance. Steps are joined to the activity ledger via `approvalId`
 * (activity entries carry it as `subjectId`), so no parallel audit log exists.
 */
export interface PlanStep {
  id: string;
  kind: 'read' | 'write';
  description: string;
  status: 'pending' | 'awaiting_approval' | 'done' | 'skipped' | 'failed';
  /** Present on write steps: the governed action this step proposes. */
  action?: AgentAction;
  /** Set once the write step is queued for approval — links to the ledger. */
  approvalId?: string;
  /** Human-readable outcome once the step resolves. */
  result?: string;
}

/**
 * A durable, resumable Orchestrator run: a Goal decomposed into a plan of steps,
 * executed end-to-end under audit. Persisted so it survives reload and resumes
 * when a gated step is approved. This is the moat piece — multi-step agent work
 * under governance — and the run + the activity ledger together are the
 * Decision Ledger.
 */
export interface OrchestratorRun {
  id: string;
  goalId: string;
  goalLabel: string;
  status: 'running' | 'awaiting_approval' | 'done' | 'cancelled';
  steps: PlanStep[];
  /** Blast-radius cap: max write steps this run may execute. */
  maxWrites: number;
  createdAt: string;
  updatedAt: string;
}

// Type-only import (erased at compile time): the saved-view filter/sort
// specs are defined next to the pure engine in src/lib/crm/viewFilter.ts
// so the persisted shape and the engine can never drift apart.
import type { ViewFilter, ViewSort } from '../crm/viewFilter';

/** The list surfaces a saved view can apply to. */
export type SavedViewEntity = 'accounts' | 'people' | 'tickets' | 'tasks';

/**
 * A saved list view: a named, reusable filter + sort spec for one of the
 * list surfaces (accounts / people / tickets / tasks). Pure data — the
 * engine that interprets it lives in src/lib/crm/viewFilter.ts, and the
 * persistence module in src/lib/db/savedViews.ts. Views are per-entity
 * and unique by (entity, name): re-saving a name replaces the view.
 */
export interface SavedView {
  /** Stable id — deterministic per (entity, name) so saves are upserts. */
  id: string;
  /** Human label shown in the view switcher, e.g. "Hot deals". */
  name: string;
  /** Which list surface this view applies to. */
  entity: SavedViewEntity;
  /** AND-combined filter clauses (see applyFilters). */
  filters: ViewFilter[];
  /** Optional sort spec (see applySort). */
  sort?: ViewSort;
  /** ISO-8601 UTC. */
  createdAt: string;
}

// Type-only import (erased at compile time): the metric vocabulary is
// defined next to the pure compute engine in src/lib/reports/reportMetrics.ts
// so a persisted report can never reference a metric the engine can't
// compute. Same pattern as ViewFilter above / ObjectDef below.
import type { ReportDisplay, ReportMetricKind } from '../reports/reportMetrics';

/**
 * A saved custom report — one build-your-own card on the Reports tab
 * (HubSpot/Databox-style "single report"). Pure data: the metric is
 * computed live by `computeReportMetric` at render time, so a saved
 * report can never hold a stale number. Persisted in `savedReports`
 * via src/lib/db/savedReports.ts (same pattern as tickets/savedViews).
 * Unique by name: re-saving a name replaces the report.
 */
export interface SavedReport {
  /** Stable id — deterministic per name so saves are upserts. */
  id: string;
  /** Human label the operator gave the card, e.g. "Pipeline this quarter". */
  name: string;
  /** Which metric the card computes (must exist in REPORT_METRICS). */
  metric: ReportMetricKind;
  /** How the card renders: headline stat or bar breakdown. */
  display: ReportDisplay;
  /** Who saved it (display name). */
  createdBy?: string;
  // Bookkeeping (ISO-8601, UTC)
  createdAt: string;
  updatedAt: string;
}

// Type-only import (erased at compile time): the custom-object schema
// shape is defined next to the pure validation engine in
// src/lib/crm/objectModel.ts so the persisted definition and the
// validator can never drift apart.
import type { ObjectDef } from '../crm/objectModel';

/**
 * A persisted custom-object definition — one workspace-defined record
 * type (the Attio-style flexible object model). Pure schema data: the
 * attribute types + validation live in src/lib/crm/objectModel.ts, and
 * the persistence module in src/lib/db/customObjects.ts. Definitions
 * are unique by `key`: re-saving a key replaces the definition.
 */
export interface CustomObjectDef extends ObjectDef {
  /** Stable id — deterministic per key so saves are upserts. */
  id: string;
  /** ISO-8601 UTC. */
  createdAt: string;
  /** ISO-8601 UTC. */
  updatedAt: string;
}

// ===========================================================================
// Dev dispatch — ticket → coding-agent pipeline (`devDispatch`).
// The PURE state machine lives in src/lib/dev/dispatchState.ts; the
// persistence module in src/lib/db/devDispatch.ts. This is only the
// persisted record shape.
// ===========================================================================

/**
 * Lifecycle of a dev dispatch. Forward-only:
 * proposed → dispatched → pr_open → merged.
 * `merged` and `failed` are terminal.
 */
export type DevDispatchState =
  | 'proposed'
  | 'dispatched'
  | 'pr_open'
  | 'merged'
  | 'failed';

/** One ticket handed to a coding agent, tracked through to a PR. */
export interface DevDispatch {
  id: string;
  /** The ticket this dispatch was minted from. */
  ticketId: string;
  /** Denormalised for display without a join. */
  ticketTitle: string;
  /** Target repository, e.g. "argo/argo-os". */
  repo?: string;
  state: DevDispatchState;
  prNumber?: number;
  prUrl?: string;
  // Bookkeeping (ISO-8601, UTC)
  createdAt: string;
  updatedAt: string;
}

// ===========================================================================
// Content pipeline items (`contentItems`) — the Content Engine's kanban:
// Idea → Draft → Scheduled → Published. Pure grouping/analytics helpers
// live in src/lib/content/pipeline.ts; this is only the persisted shape.
// ===========================================================================

/** Kanban stage of a content item. Order matters: Idea → Draft → Scheduled → Published. */
export type ContentItemStatus = 'idea' | 'draft' | 'scheduled' | 'published';

/** The five operator-facing content types the studio produces. */
export type ContentItemType =
  | 'linkedin-post'
  | 'x-thread'
  | 'x-post'
  | 'blog'
  | 'email'
  | 'ig-caption';

/** One piece of content moving through the Idea → Published pipeline. */
export interface ContentItem {
  /** Sequential id, e.g. "CNT-001". */
  id: string;
  title: string;
  type: ContentItemType;
  status: ContentItemStatus;
  /** The draft text itself. May be empty for a bare idea. */
  body: string;
  /** Best-practice score 0..100 stamped by the view's scorer; null = unscored. */
  score: number | null;
  /**
   * True when `score` came from the deterministic local simulation rather
   * than the real AQI engine — the honesty flag the UI renders as a
   * 'Simulated' pill. Optional + additive: older records need no migration.
   */
  scoreSimulated?: boolean;
  /** ISO date the item is slotted to go out. Only meaningful when scheduled. */
  scheduledFor: string | null;
  /** Starter template this item was seeded from, if any. */
  templateId: string | null;
  /**
   * Honest outcome of the most recent engine publish attempt for this draft,
   * stamped by the `publish_content` executor. Distinguishes a real live post
   * from the engine's dry-run safety default — the UI must render dry runs
   * as "no live post", never as published. Optional + additive: older
   * records need no migration.
   */
  lastPublish?: {
    status: 'published' | 'dry_run' | 'error' | 'not_configured';
    /** Live post URL when the engine returned one. */
    url: string | null;
    /** Engine message / error detail when present. */
    message: string | null;
    channel: string;
    /** ISO-8601 UTC timestamp of the attempt. */
    at: string;
  };
  // Bookkeeping (ISO-8601, UTC)
  createdAt: string;
  updatedAt: string;
}

/** Names of every collection persisted in the local store. */
export type CollectionName =
  | 'people'
  | 'accounts'
  | 'appUsers'
  | 'tickets'
  | 'documents'
  | 'tasks'
  | 'agents'
  | 'roadmap'
  | 'outbox'
  | 'approvals'
  | 'agentPrefs'
  | 'aqiHistory'
  | 'healthHistory'
  | 'brainFacts'
  | 'brainEvents'
  | 'activity'
  | 'orchestratorRuns'
  | 'savedViews'
  | 'customObjects'
  | 'outreachSequences'
  | 'outreachEnrollments'
  | 'deliverables'
  | 'projects'
  | 'devDispatch'
  | 'signatureRequests'
  | 'contentItems'
  | 'ledger'
  | 'savedReports'
  | 'agentFeed'
  | 'automations';

/** Map a collection name to its record type. */
export interface CollectionTypes {
  people: Person;
  accounts: Account;
  appUsers: AppUser;
  tickets: Ticket;
  documents: BusinessDoc;
  tasks: ProjectTask;
  agents: DeployedAgent;
  roadmap: RoadmapItem;
  outbox: OutboxDoc;
  approvals: ApprovalItem;
  agentPrefs: AgentPref;
  aqiHistory: AqiSnapshot;
  healthHistory: CustomerHealthSnapshot;
  brainFacts: BrainFact;
  brainEvents: BrainEvent;
  activity: ActivityEntry;
  orchestratorRuns: OrchestratorRun;
  savedViews: SavedView;
  customObjects: CustomObjectDef;
  outreachSequences: OutreachSequence;
  outreachEnrollments: OutreachEnrollment;
  deliverables: Deliverable;
  projects: Project;
  devDispatch: DevDispatch;
  signatureRequests: SignatureRequest;
  contentItems: ContentItem;
  ledger: DecisionLedgerEntry;
  savedReports: SavedReport;
  agentFeed: AgentFeedEntry;
  automations: AutomationRecord;
}

// ===========================================================================
// Outreach sequences — governed multi-step cadences (`outreachSequences`).
// The PURE model + scheduling + suppression logic lives in
// src/lib/crm/outreach.ts; this is only the persisted record shape.
// Sending is a later, gated, credential'd atom — nothing stored here
// implies a send happened.
// ===========================================================================

/** A persisted outreach sequence — a governed cadence definition. */
export interface OutreachSequence {
  id: string;
  name: string;
  /**
   * Lifecycle of the sequence itself. 'active' sequences are eligible for
   * the runner's due-step engine; 'draft'/'archived' are excluded. Optional
   * + additive so existing records (no `status`) are treated as active.
   */
  status?: 'draft' | 'active' | 'archived';
  /** Ordered steps; each due `dayOffset` days after enrollment. */
  steps: SequenceStep[];
  // Bookkeeping (ISO-8601, UTC)
  createdAt: string;
  updatedAt: string;
}

/**
 * A persisted enrollment — one contact walking through one sequence.
 *
 * The due-step engine (src/lib/crm/sequenceRunner.ts) reads these plus the
 * sequence definition to decide which (enrollment, step) pairs are due to
 * send NOW. Nothing here implies a send happened on its own — the runner
 * agent only PROPOSES `send_sequence_step` actions into the approval queue;
 * the executor advances `currentStep` / `lastSentAt` on approval.
 */
export interface OutreachEnrollment {
  id: string;
  /** The sequence this enrollment walks through. */
  sequenceId: string;
  /** The contact being enrolled. */
  personId: string;
  /** Denormalised for display without a join. */
  personName?: string;
  /** ISO-8601 UTC timestamp the contact was enrolled. */
  enrolledAt: string;
  /** Index into the sequence's `steps` of the NEXT step to send. */
  currentStep: number;
  status: 'active' | 'completed' | 'stopped';
  /** Why the enrollment stopped, e.g. 'replied'. */
  stoppedReason?: string;
  /** ISO-8601 UTC timestamp of the last approved send. */
  lastSentAt?: string;
  // Bookkeeping (ISO-8601, UTC)
  createdAt: string;
  updatedAt: string;
}
